import { getCachedDocumentMetas, removeDocument } from './documentCache';
import { getStorageBudget } from './storageManager';

export interface EvictionOptions {
  targetBytes: number;
  protectedProcessoIds?: string[];
}

export async function evictLRU(options: EvictionOptions): Promise<number> {
  try {
    const metas = await getCachedDocumentMetas();
    const protectedSet = new Set(options.protectedProcessoIds ?? []);

    const evictable = metas
      .filter((m) => !m.processoId || !protectedSet.has(m.processoId))
      .sort(
        (a, b) =>
          new Date(a.lastAccessedAt).getTime() -
          new Date(b.lastAccessedAt).getTime(),
      );

    let freedBytes = 0;

    for (const doc of evictable) {
      if (freedBytes >= options.targetBytes) break;

      await removeDocument(doc.id);
      freedBytes += doc.size;
    }

    return freedBytes;
  } catch {
    return 0;
  }
}

export async function evictIfNeeded(): Promise<void> {
  try {
    const budget = await getStorageBudget();
    if (!budget.isLow) return;

    // Evict until we're at 60% usage
    const targetUsage = Math.floor(budget.totalBytes * 0.6);
    const bytesToFree = Math.max(0, budget.usedBytes - targetUsage);

    if (bytesToFree > 0) {
      await evictLRU({ targetBytes: bytesToFree });
    }
  } catch {
    // Degrade silently
  }
}
